import React, { useState, useEffect, useRef } from 'react';
import './CreatingView.css';

const CreatingView = ({ onComplete, documentTitle }) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const startTime = Date.now();

    // Simple progress animation
    const progressInterval = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000; // seconds

      // Update progress bar (complete in 10 seconds)
      const newProgress = Math.min((elapsed / 10) * 100, 100);
      setProgress(newProgress);

      // Stop at 100%
      if (newProgress >= 100) {
        clearInterval(progressInterval);
        setIsComplete(true);
      }
    }, 100);

    return () => {
      clearInterval(progressInterval);
    };
  }, []);

  return (
    <div className="creating-container">
      <div className="creating-content">
        {/* Centered loading card */}
        <div className={`loading-card ${isComplete ? 'completed' : ''}`}>
          {!isComplete ? (
            <>
              <h1 className="loading-title">Creating your {documentTitle}</h1>
              <p className="loading-subtitle">Hang tight, this won't take long</p>

              {/* Simple progress bar */}
              <div className="simple-progress-bar">
                <div className="simple-progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
            </>
          ) : (
            <>
              {/* Completion state */}
              <img
                src="/frontpage.png"
                alt="Document preview"
                className="preview-image"
              />
              <h2 className="completion-title">Your {documentTitle} is ready</h2>
              <div className="completion-buttons">
                <button className="completion-button secondary" onClick={() => window.location.reload()}>
                  Try another
                </button>
                <button className="completion-button primary">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 11C12 11.5523 11.5523 12 11 12H1C0.447715 12 0 11.5523 0 11V10H12V11ZM7 4.17285L9.04297 2.12988L10.457 3.54395L6 8.00098L1.54297 3.54395L2.95703 2.12988L5 4.17285V0H7V4.17285Z" fill="white"/>
                  </svg>
                  Download presentation
                </button>
              </div>
            </>
          )}
        </div>

        {/* Tagline */}
        <p className="tagline">
          There's a reason teams never go back. Templafy is built for the people doing the work.
        </p>

        {/* Bottom section with video and features */}
        <div className="bottom-section">
          {/* Video */}
          <div className="video-wrapper">
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/ylK3BjelPu8?autoplay=1&mute=1&controls=1&modestbranding=1"
                title="Templafy Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          {/* Features list */}
          <div className="features-wrapper">
            <ul className="features-list">
              <li className="feature-item">
                <svg className="checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="#464646" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="feature-text">
                  <strong>AI Drafting:</strong> Instant documents
                </div>
              </li>
              <li className="feature-item">
                <svg className="checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="#464646" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="feature-text">
                  <strong>Native Tools:</strong> Works inside Office/CRM
                </div>
              </li>
              <li className="feature-item">
                <svg className="checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="#464646" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="feature-text">
                  <strong>Auto-Compliance:</strong> Brand & legal safety
                </div>
              </li>
              <li className="feature-item">
                <svg className="checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="#464646" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="feature-text">
                  <strong>One-Click Assets:</strong> Fast access to slides
                </div>
              </li>
              <li className="feature-item">
                <svg className="checkmark" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10l4 4 8-8" stroke="#464646" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="feature-text">
                  <strong>Time Saved:</strong> 4 hours weekly
                </div>
              </li>
            </ul>

            <a href="https://www.templafy.com" className="learn-more-link" target="_blank" rel="noopener noreferrer">
              Learn more about Templafy →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatingView;
